﻿import { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Package, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCompany } from "@/lib/CompanyProvider";
import type { InventoryItem } from "@/hooks/inventory/useInventoryData";
import {
  useInventoryData } from "@/hooks/inventory/useInventoryData";

import { InventoryMetrics } from "@/components/inventory/InventoryMetrics";
import { PendingRequests } from "@/components/inventory/PendingRequests";
import { InventoryList } from "@/components/inventory/InventoryList";
import { InventoryModal } from "@/components/inventory/InventoryModal";

export const Route = createFileRoute("/app/inventory")({
  component: InventoryPage,
});

function InventoryPage() {
  const { activeCompanyId } = useCompany();

  const [searchTerm, setSearchTerm] = useState("");
  const [isCompact, setIsCompact] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [categoryVal, setCategoryVal] = useState("");
  const [isNewItemModalOpen, setIsNewItemModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<"details" | "history">("details");

  const { inventory, requests, loadingInv, saveItem, resolveRequest } =
    useInventoryData(activeCompanyId);

  const filteredInventory = useMemo(() => {
    return inventory.filter(
      (i) =>
        i.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.category.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [inventory, searchTerm]);

  const uniqueCategories = useMemo(() => {
    return Array.from(new Set(inventory.map((i) => i.category))).filter(
      Boolean,
    );
  }, [inventory]);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-white tracking-tight flex items-center gap-3">
            <Package className="text-blue-500" size={32} />
            Gestão de Estoque
          </h1>
          <p className="text-slate-400 mt-1">
            Acompanhe insumos, aprove pedidos e evite faltas.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setIsCompact(!isCompact)}
            className="border-slate-700 text-slate-300 hover:text-white bg-slate-900/50"
          >
            {isCompact ? "Modo Normal" : "Modo Compacto"}
          </Button>
          <Button
            onClick={() => {
              setIsNewItemModalOpen(true);
              setModalTab("details");
            }}
            className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-900/20"
          >
            <Plus size={18} className="mr-2" /> Novo Insumo
          </Button>
        </div>
      </div>

      <InventoryMetrics inventory={inventory} requests={requests} />

      <PendingRequests requests={requests} resolveRequest={resolveRequest} />

      <InventoryList
        filteredInventory={filteredInventory}
        loadingInv={loadingInv}
        isCompact={isCompact}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        openEditModal={(item) => {
          setEditingItem(item);
          setCategoryVal(item.category);
          setModalTab("details");
        }}
      />

      <InventoryModal
        isOpen={isNewItemModalOpen || !!editingItem}
        setIsOpen={(val) => {
          if (!val) {
            setIsNewItemModalOpen(false);
            setEditingItem(null);
          } else {
            setIsNewItemModalOpen(true);
          }
        }}
        editingItem={editingItem}
        setEditingItem={setEditingItem}
        categoryVal={categoryVal}
        setCategoryVal={setCategoryVal}
        uniqueCategories={uniqueCategories}
        saveItem={saveItem}
        activeCompanyId={activeCompanyId}
        modalTab={modalTab}
        setModalTab={setModalTab}
      />
    </div>
  );
}
